/**
 * Validador de CPF e CNPJ Alfanumérico
 * Conforme Nota Técnica Conjunta 2025.001 - CNPJ Alfanumérico
 * Instrução Normativa RFB nº 2229/2024
 */

export class CpfValidator {
  private static readonly PESO_CPF_1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
  private static readonly PESO_CPF_2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
  private static readonly REGEX_CPF = /^\d{11}$/;
  private static readonly REGEX_MASCARA = /[.\-]/g;

  static isValid(value: string): boolean {
    if (!value) return false;

    const cpfLimpo = value.replace(this.REGEX_MASCARA, '');

    if (!this.REGEX_CPF.test(cpfLimpo)) return false;
    if (/^(\d)\1{10}$/.test(cpfLimpo)) return false;

    const digitos = cpfLimpo.split('').map(Number);

    const dv1 = this.calculaDV(digitos.slice(0, 9), this.PESO_CPF_1);
    if (dv1 !== digitos[9]) return false;

    const dv2 = this.calculaDV(digitos.slice(0, 10), this.PESO_CPF_2);
    if (dv2 !== digitos[10]) return false;

    return true;
  }

  private static calculaDV(digitos: number[], pesos: number[]): number {
    const soma = digitos.reduce((acc, dig, i) => acc + dig * pesos[i], 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  }
}

export class CnpjValidator {
  private static readonly TAMANHO_CNPJ_SEM_DV = 12;
  private static readonly REGEX_CNPJ_SEM_DV = /^[A-Z\d]{12}$/;
  private static readonly REGEX_CNPJ = /^[A-Z\d]{12}\d{2}$/;
  private static readonly REGEX_MASCARA = /[.\/-]/g;
  private static readonly REGEX_CARACTERES_NAO_PERMITIDOS = /[^A-Z\d.\/-]/i;
  private static readonly VALOR_BASE = '0'.charCodeAt(0);
  private static readonly PESOS_DV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  private static readonly CNPJ_ZERADO = '00000000000000';

  static isValid(value: string): boolean {
    if (!value) return false;

    if (this.REGEX_CARACTERES_NAO_PERMITIDOS.test(value)) return false;

    const cnpjLimpo = this.removeMascara(value);

    if (!this.REGEX_CNPJ.test(cnpjLimpo)) return false;
    if (cnpjLimpo === this.CNPJ_ZERADO) return false;

    const dvCalculado = this.calculaDV(cnpjLimpo.substring(0, this.TAMANHO_CNPJ_SEM_DV));
    const dvInformado = cnpjLimpo.substring(this.TAMANHO_CNPJ_SEM_DV);

    return dvInformado === dvCalculado;
  }

  static calculaDV(cnpj: string): string {
    if (this.REGEX_CARACTERES_NAO_PERMITIDOS.test(cnpj)) {
      throw new Error('CNPJ contém caracteres não permitidos');
    }

    const cnpjLimpo = this.removeMascara(cnpj);

    if (!this.REGEX_CNPJ_SEM_DV.test(cnpjLimpo)) {
      throw new Error('Não é possível calcular o DV pois o CNPJ fornecido é inválido');
    }

    if (cnpjLimpo === this.CNPJ_ZERADO.substring(0, this.TAMANHO_CNPJ_SEM_DV)) {
      throw new Error('Não é possível calcular o DV pois o CNPJ fornecido é inválido');
    }

    let somatorioDV1 = 0;
    let somatorioDV2 = 0;

    for (let i = 0; i < this.TAMANHO_CNPJ_SEM_DV; i++) {
      const asciiDigito = cnpjLimpo.charCodeAt(i) - this.VALOR_BASE;
      somatorioDV1 += asciiDigito * this.PESOS_DV[i + 1];
      somatorioDV2 += asciiDigito * this.PESOS_DV[i];
    }

    const dv1 = somatorioDV1 % 11 < 2 ? 0 : 11 - (somatorioDV1 % 11);
    somatorioDV2 += dv1 * this.PESOS_DV[this.TAMANHO_CNPJ_SEM_DV];
    const dv2 = somatorioDV2 % 11 < 2 ? 0 : 11 - (somatorioDV2 % 11);

    return `${dv1}${dv2}`;
  }

  private static removeMascara(cnpj: string): string {
    return cnpj.replace(this.REGEX_MASCARA, '').toUpperCase();
  }
}
